/**
 * The personalization funnel, end to end.
 *
 * Ported from the approved prototype (funnel.build.html) with the two fakes
 * replaced by the real thing:
 *
 *   • the "creating the avatar" step actually calls the Character Editor's
 *     photo → character analysis (`analyzePhoto`, proxied by the dev-server
 *     plugin so no key reaches the browser) and paints the result on the real
 *     Rive child (see AvatarStage);
 *   • the child's captured PNG is what every poster, the billboard and the
 *     paywall preview show — the prototype's single hard-coded head and two
 *     base64 videos are gone.
 *
 * A new screen sits between the reveal and the paywall: the catalogue the
 * avatar lands in (`stream`), whose PLAY opens the paywall — the funnel's actual
 * conversion moment.
 *
 * This IS the consumer surface — cartoontimeplus.com. Nothing here writes a
 * Character Photo anywhere: the upload stays in the tab, and only the derived
 * look is kept.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, createContext, useContext } from 'react';
import {
  analyzePhoto,
  getPreset,
  tryParseResult,
  validateResult,
  type AnalysisResult,
  type Provider,
} from '@editor/photoAnalysis';
import { AvatarStage } from './AvatarStage';
import { defaultLook, lookFromAnalysis, type FunnelLook } from './avatarLook';
import type { AvatarShots } from './captureAvatar';
import { HERO_AVATAR, HERO_AVATAR_NARROW, HERO_COSTUME, HERO_SCENE, HERO_TAGLINE, POSTERS, PREVIEW_POSTER_ID, TOPIC_POSTERS, type AvatarPlacement, type Poster } from './posters';
import { COSTUMES, type Costume, COSTUME_VERSION } from './costumes';
import {
  AGE_BANDS, FACTS, FAITH_BRANCHES, FAITH_CHOICE, FAITH_GROUPS, FAMILY, MOODS,
  PACE, PRONOUNS, TOPICS_BY_AGE, type Fact, type Option,
  topicClipsFor,
} from './onboarding';
import { PLANS, TEST_PLAN, checkoutReturn, onReturnFromCheckout, startCheckout, type PlanId } from './billing';
import { PLAN_AMOUNT, firstSightOf, recallPlan, rememberPlan, track, trackCustom } from './pixel';
import { clearSnapshot, loadSnapshot, saveSnapshot } from './resume';
import { collect } from './collect';
import { analytics } from './analytics';
import { CartoonPlayer } from './cartoon/CartoonPlayer';
import { requestNarration } from './cartoon/narration';
import { freeScenesFor, useCartoonGate } from './cartoon/gate';
import { trackScene, trackWatch } from './cartoon/watchEvents';
import { GatePicker, useSecretTaps } from './cartoon/GatePicker';
import './funnel.css';
import './funnel-v3.css';

// v2 ships only in development, where the design switch can still reach it.
// A statically imported stylesheet is always in the bundle, whether any element
// carries its class or not, and the published site is v3 only.
if (import.meta.env.DEV) void import('./funnel-v2.css');

// ── Flow definition (unchanged from the prototype, plus `stream`) ─────────────

/**
 * Which vision model reads the photo.
 *
 * Fixed rather than chosen in the UI: a parent has no way to judge this, and it
 * is not their decision to make. Any vision model OpenRouter serves works here —
 * `MODELS` in `@editor/photoAnalysis` is the Studio picker's list, not a limit,
 * and it predates the Gemini 3.x generation.
 *
 * Compare candidates with the bake-off page (`?compare`) before changing this:
 * what matters is not whether a model answers, but whether its answer lands
 * inside the Character Editor's vocabulary — an out-of-vocabulary value is
 * skipped when the look is built and quietly becomes a default.
 */
const ANALYSIS_PROVIDER: Provider = 'openrouter';
const ANALYSIS_MODEL = 'google/gemini-3.1-flash-lite';

/**
 * Which visual identity to render.
 *
 * **The published site is v3 and only v3.** v1 (the purple prototype) and v2
 * (warm paper) survive for comparison during development, reachable through the
 * switch or `?design=`; in production the query parameter is ignored, because
 * honouring it would serve a half-styled page — v2's stylesheet is not in the
 * production bundle at all.
 *
 * All three render the SAME flow: a design version is a stylesheet, not a fork.
 */
type Design = 'v1' | 'v2' | 'v3';
const DESIGNS: Design[] = ['v1', 'v2', 'v3'];
const DEFAULT_DESIGN: Design = 'v3';

const designFromUrl = (): Design => {
  if (!import.meta.env.DEV) return DEFAULT_DESIGN;
  const v = new URLSearchParams(location.search).get('design') as Design | null;
  return v && DESIGNS.includes(v) ? v : DEFAULT_DESIGN;
};

type StepType =
  | 'hero' | 'choice' | 'multi' | 'name' | 'fact'
  | 'photo' | 'gen' | 'reveal' | 'stream' | 'pay' | 'done';

/** What a parent has answered so far. Single-select holds a string, multi an array. */
export type Answers = Record<string, string | string[]>;

interface Step {
  type: StepType;
  key?: string;
  eyebrow?: string;
  q?: string;
  sub?: string;
  grid?: boolean;
  /** Static options, or derived ones — topics depend on the age already given. */
  opts?: Option[] | ((a: Answers) => Option[]);
  /** A step that only applies to some answers (the faith branch). */
  when?: (a: Answers) => boolean;
  fact?: Fact;
}

/** A step's options, resolving the derived form (topics depend on age). */
const optionsFor = (step: Step, a: Answers): Option[] =>
  typeof step.opts === 'function' ? step.opts(a) : step.opts ?? [];

/**
 * Fill `{name}` in authored copy.
 *
 * The questions before the name is asked cannot use it, which is why the name
 * sits third: everything after it can. Copy stays declarative in the step
 * definitions rather than being assembled with string concatenation at render.
 */
const fill = (text: string | undefined, name: string): string =>
  (text ?? '').replace(/\{name\}/g, name);

const one = (a: Answers, k: string): string => (typeof a[k] === 'string' ? (a[k] as string) : '');
const many = (a: Answers, k: string): string[] => (Array.isArray(a[k]) ? (a[k] as string[]) : []);

/**
 * The question set, aligned to the product's own onboarding.
 *
 * The name is asked THIRD rather than last: every screen after it can then say
 * the child's name, which is the whole argument the funnel is making. Two easy
 * taps come first so the parent is not typing before anything has happened.
 */
const QUESTIONS: Step[] = [
  {
    type: 'choice', key: 'pronouns', eyebrow: 'To start',
    q: 'Who is the cartoon for?',
    sub: 'They will be the hero of every episode.',
    opts: PRONOUNS,
  },
  {
    type: 'choice', key: 'age', eyebrow: 'Age', q: 'How old are they?',
    sub: 'This shapes the stories CartoonTime starts with.',
    opts: AGE_BANDS,
  },
  { type: 'name' },
  { type: 'fact', fact: FACTS[0] },
  {
    type: 'multi', key: 'topics', eyebrow: 'Where to start',
    q: 'What should {name} master next?',
    sub: 'Pick the things you want to help {name} get the hang of. Each one becomes a story.',
    // Keyed off the age already answered, so the list is about this child.
    opts: (a) => TOPICS_BY_AGE[one(a, 'age')] ?? TOPICS_BY_AGE['age-3-4'],
  },
  { type: 'fact', fact: FACTS[3] },
  {
    type: 'multi', key: 'moods', eyebrow: 'Stories',
    q: 'What kind of stories does {name} love?',
    sub: 'We’ll lean this way in what we recommend.',
    grid: true, opts: MOODS,
  },
  {
    type: 'choice', key: 'pace', eyebrow: 'Style', q: 'What style of cartoon do you want for {name}?',
    opts: PACE,
  },
  { type: 'fact', fact: FACTS[1] },
  {
    type: 'choice', key: 'faith', eyebrow: 'Your family’s values',
    q: 'Would you like stories that build on your faith?',
    sub: 'Entirely optional. CartoonTime never assumes. Faith stories appear only if you choose them.',
    opts: FAITH_CHOICE,
  },
  {
    type: 'choice', key: 'faithGroup', eyebrow: 'Your tradition',
    q: 'Which tradition should CartoonTime follow for {name}?',
    sub: 'Stories follow the rules of the tradition you choose. Your child is always a listener or a helper, never a sacred figure.',
    opts: FAITH_GROUPS,
    when: (a) => one(a, 'faith') === 'yes',
  },
  {
    type: 'choice', key: 'faithBranch', eyebrow: 'Your tradition',
    q: 'Anything more specific?',
    sub: 'So the stories follow the right customs. Skip it and CartoonTime stays general.',
    opts: (a) => FAITH_BRANCHES[one(a, 'faithGroup')] ?? [],
    when: (a) => one(a, 'faith') === 'yes' && (FAITH_BRANCHES[one(a, 'faithGroup')]?.length ?? 0) > 0,
  },
  {
    type: 'multi', key: 'family', eyebrow: 'The family',
    q: 'Who else is in {name}’s world?',
    sub: 'Brothers, sisters and grandparents can be in the stories too.',
    grid: true, opts: FAMILY,
  },
  { type: 'fact', fact: FACTS[2] },
];

const STEPS: Step[] = [
  { type: 'hero' },
  ...QUESTIONS,
  { type: 'photo' },
  { type: 'gen' },
  { type: 'reveal' },
  { type: 'stream' },
  { type: 'pay' },
  { type: 'done' },
];

/** Steps that count toward the progress bar — the fact screens are not work. */
const COUNTED: StepType[] = ['choice', 'multi', 'name', 'photo'];

const PAY_STEP = STEPS.findIndex((s) => s.type === 'pay');
const DONE_STEP = STEPS.findIndex((s) => s.type === 'done');

/**
 * Where to start.
 *
 * Stripe Checkout is a different site, so a parent coming back from it arrives
 * on a fresh page with none of the funnel's state — there is no avatar and no
 * name to return to. Rather than dropping them at the hero to answer everything
 * again, a completed payment opens the confirmation and a cancelled one opens
 * the paywall they backed out of.
 */
/** The snapshot saved on the way to Stripe, if this load is the way back. */
const RESUME = loadSnapshot();

/**
 * /onboarding/dashboard (or ?dashboard): the catalogue straight away with a
 * built-in demo child (the generated blonde girl from the homepage cards, not
 * a real child), for checking posters and costumes without the whole flow.
 */
const DEMO = /\/onboarding\/dashboard\/?$/.test(location.pathname) || new URLSearchParams(location.search).has('dashboard');
const DEMO_SHOTS: AvatarShots = { full: '', head: '', face: '/demo/face.png', fullAspect: 0.6, faceAspect: 299 / 314 };
const STREAM_STEP = STEPS.findIndex((s) => s.type === 'stream');
/** The cartoon on the catalogue's billboard — a folder under assets/cartoons/. */
const CARTOON_ID = 'zoo';

const startStep = (): number => {
  if (DEMO) return STREAM_STEP;
  const back = checkoutReturn();
  if (back === 'success') return DONE_STEP;
  if (back === 'cancelled') return PAY_STEP;
  // No query, but a fresh snapshot: the Back button out of Checkout. Same place.
  if (RESUME) return PAY_STEP;
  return 0;
};

// ── Shared bits ───────────────────────────────────────────────────────────────

const BOKEH = [[40, 8, 12], [70, 18, 7], [15, 30, 9], [86, 45, 6], [25, 70, 11], [60, 80, 8], [8, 55, 7], [92, 72, 10], [50, 40, 5]];

/**
 * A silent looping clip.
 *
 * Autoplay is skipped under `prefers-reduced-motion`, which leaves the first
 * frame showing — the illustration still does its job, the movement does not
 * happen to someone it makes unwell. `muted` + `playsInline` are what let it
 * autoplay on iOS at all; without them Safari shows a black box with a play
 * button in the middle of the funnel.
 */
function Clip({ src, srcs, className }: { src?: string; srcs?: string[]; className?: string }) {
  // A playlist plays its clips one after another and wraps around; a single
  // clip simply loops. (The topic clips: one per picked topic, in turn.)
  const list = useMemo(() => (srcs && srcs.length ? srcs : src ? [src] : []), [src, srcs]);
  const [i, setI] = useState(0);
  useEffect(() => { setI(0); }, [list]);
  const current = list[Math.min(i, list.length - 1)];
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const p = el.play();
    if (p && typeof p.catch === 'function') p.catch(() => { /* blocked — first frame stands */ });
  }, [current]);
  if (!current) return null;
  return (
    <video
      ref={ref} className={className} src={current}
      muted loop={list.length === 1} playsInline preload="auto" disablePictureInPicture
      onEnded={list.length > 1 ? () => setI((n) => (n + 1) % list.length) : undefined}
      aria-hidden
    />
  );
}

/**
 * The real photo the hero clip was made from, pinned to the clip like a
 * polaroid: "this photo → this cartoon". Lives at `assets/hero-photo.webp`;
 * until that file exists the figure hides itself so the hero still reads.
 */
function HeroPhoto() {
  const [missing, setMissing] = useState(false);
  if (missing) return null;
  return (
    <figure className="herophoto">
      <img src="/hero-photo.webp" alt="The real child the cartoon hero was made from" onError={() => setMissing(true)} />
      <figcaption>From this photo</figcaption>
    </figure>
  );
}

function Bokeh() {
  return (
    <div className="bokeh" aria-hidden>
      {BOKEH.map(([left, top, size], i) => (
        <i
          key={i}
          // Position and size travel as CUSTOM PROPERTIES rather than as inline
          // width/height: an inline value beats any stylesheet, and a design
          // version that wants different shapes here could not override it.
          style={{
            '--bk-x': `${left}%`, '--bk-y': `${top}%`, '--bk-size': `${size * 7}px`,
            animationDuration: `${6 + size}s`, animationDelay: `${-size}s`,
          } as CSSProperties}
        />
      ))}
    </div>
  );
}

/** The child composited into a scene, at a poster's placement. */
/**
 * The child dressed for the scene: a painted costume body with the captured
 * head dropped onto it. The body image carries the head's slot (costumes.ts),
 * so the head is sized to the slot's width — the hair's width — and its chin
 * sits a touch below the slot's bottom, over the costume's collar.
 */
/**
 * The head is drawn at a FIXED height relative to the body (the generated
 * bodies drew their head blobs at all sizes, so scaling the blob gave small
 * heads on some costumes), a touch below the blob, and BEHIND the body: it
 * shows through the head hole and the collar or hood covers the neck, so it
 * never floats above a turtleneck and no hole edge shows (Jernej, 2026-09-08).
 * scripts/composite-home-cards.py uses the same numbers and the same order.
 */
/**
 * The child's skin tone (index into CHARACTER_SKIN_PALETTE, from the photo
 * analysis) picks the costume body: each costume ships nine bodies, one per
 * tone, made by scripts/skin-variants.py from a skin mask. Default is the
 * lightest-but-one tone the demo cards use.
 */
const ToneContext = createContext(7);
const bodyForTone = (body: string, tone: number) => body.replace(/\.webp$/, `-${tone}.webp?v=${COSTUME_VERSION}`);

/**
 * The face sits where `costume.face` says: a height, a centre and a chin
 * height, all fractions of the body's own box, tuned by hand on the Figma
 * artboards and read back by scripts/write-costumes-ts.py (Jernej, 2026-09-10).
 * It is drawn ON TOP of the body, as it is in Figma.
 * scripts/composite-home-cards.py uses the same three numbers.
 */
function Costumed({
  face, faceAspect, costume, at,
}: { face: string; faceAspect: number; costume: Costume; at: AvatarPlacement }) {
  const f = costume.face;
  const tone = useContext(ToneContext);
  const faceStyle: CSSProperties = {
    height: `${f.h * 100}%`,
    width: 'auto',
    aspectRatio: faceAspect,
    left: `${f.cx * 100}%`,
    bottom: `${f.bottom * 100}%`,
  };
  return (
    <span
      className="who costumed"
      style={{
        height: `${at.h}%`,
        aspectRatio: costume.aspect,
        left: `${at.x}%`,
        bottom: `${at.bottom}%`,
        transform: `translateX(-50%) rotate(${at.rotate ?? 0}deg) scaleX(${at.flip ? -1 : 1})`,
      }}
    >
      {/* `behind` tucks the face under the body, for a pose where a hand
          crosses the face — the phone held to the ear in "Calling for help". */}
      {f.behind && <img className="face" src={face} alt="" draggable={false} style={faceStyle} />}
      <img className="body" src={bodyForTone(costume.body, tone)} alt="" draggable={false} />
      {!f.behind && <img className="face" src={face} alt="" draggable={false} style={faceStyle} />}
    </span>
  );
}

/** The child in the scene: costumed when the poster has a costume and the capture gave a face, else the full figure. */
function Child({ shots, at, costume }: { shots: AvatarShots | null; at: AvatarPlacement; costume?: string }) {
  const c = costume ? COSTUMES[costume] : undefined;
  if (c && shots?.face) return <Costumed face={shots.face} faceAspect={shots.faceAspect} costume={c} at={at} />;
  return <Who src={shots?.full ?? null} at={at} aspect={shots?.fullAspect ?? 0.6} />;
}

function Who({ src, at, aspect }: { src: string | null; at: AvatarPlacement; aspect: number }) {
  if (!src) return null;
  // Height drives the size (a placement is authored against the poster's height);
  // width follows from the captured cut-out's own aspect so nothing is squashed.
  return (
    <img
      className="who"
      src={src}
      alt=""
      style={{
        height: `${at.h}%`,
        width: 'auto',
        aspectRatio: aspect,
        left: `${at.x}%`,
        bottom: `${at.bottom}%`,
        transform: `translateX(-50%) rotate(${at.rotate ?? 0}deg) scaleX(${at.flip ? -1 : 1})`,
      }}
    />
  );
}

/**
 * A pick-as-many-as-you-like step.
 *
 * Unlike the single-select steps it cannot advance on tap, so it carries its own
 * Continue — and that button stays enabled with nothing picked. A parent whose
 * child is between stages should not be forced to claim a struggle they do not
 * have just to get past the screen.
 */
function MultiStep({
  step, options, picked, onToggle, onContinue, name,
}: {
  step: Step; options: Option[]; picked: string[];
  onToggle: (v: string) => void; onContinue: () => void; name: string;
}) {
  return (
    <div>
      <div className="eyebrow">{step.eyebrow}</div>
      <h1 className="q">{fill(step.q, name)}</h1>
      {step.sub && <p className="sub">{fill(step.sub, name)}</p>}
      <div className={`opts${step.grid ? ' grid2' : ''}`}>
        {options.map((o) => {
          const on = picked.includes(o.v);
          return (
            <button key={o.v} className={`opt${on ? ' sel' : ''}`} onClick={() => onToggle(o.v)}>
              {o.emo && <span className="emo">{o.emo}</span>}
              <span className="optlabel">
                {o.label}
                {o.copy && <i>{o.copy}</i>}
              </span>
              <span className="tick">{on ? '✓' : ''}</span>
            </button>
          );
        })}
      </div>
      <button className="cta" onClick={onContinue}>
        {picked.length ? 'Continue' : 'Skip for now'}
      </button>
    </div>
  );
}

/** Splits "Name: blurb" so the title leads and the blurb sits back. */
function PosterLabel({ text }: { text: string }) {
  const i = text.indexOf(': ');
  if (i === -1) return <span className="label"><b>{text}</b></span>;
  return (
    <span className="label">
      <b>{text.slice(0, i)}</b>
      <i>{text.slice(i + 2)}</i>
    </span>
  );
}

function PosterCard({
  poster, name, shots, onClick, big,
}: { poster: Poster; name: string; shots: AvatarShots | null; onClick?: () => void; big?: boolean }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag className={`poster${big ? ' big' : ''}`} onClick={onClick} type={onClick ? 'button' : undefined}>
      <span className="art">{poster.scene}</span>
      <Child shots={shots} at={poster.avatar} costume={poster.costume} />
      <span className="vignette" />
      {poster.overlay && <span className="word">{poster.overlay(name)}</span>}
    </Tag>
  );
}

// ── The funnel ────────────────────────────────────────────────────────────────

export default function FunnelFlow() {
  const [cur, setCur] = useState(startStep);
  const [answers, setAnswers] = useState<Answers>(() => RESUME?.answers ?? (DEMO ? { pronouns: 'she', age: 'age-3-4' } : {}));
  /**
   * The faith branch only exists for some answers, so "the next step" is not
   * "the next index" — a skipped step must be stepped over in both directions.
   *
   * Read through a ref kept current during render, NOT through the `answers`
   * closure: an option's handler sets the answer and then calls next() on a
   * 240ms timer, so a closure captured at click time still says the question was
   * unanswered and skips the very branch the answer just opened.
   */
  const answersRef = useRef<Answers>(answers);
  answersRef.current = answers;
  const visible = useCallback((i: number) => {
    const st = STEPS[i];
    return !!st && (!st.when || st.when(answersRef.current));
  }, []);
  const [name, setName] = useState(RESUME?.name ?? (DEMO ? 'Mia' : ''));
  const [photo, setPhoto] = useState<string | null>(null);
  const [plan, setPlan] = useState<PlanId>(RESUME?.plan ?? 'year');
  const [design, setDesign] = useState<Design>(designFromUrl);

  // Checkout leaves the site, so "opening" is a state the button has to hold:
  // the redirect takes a moment and a second tap would open a second session.
  const [opening, setOpening] = useState(false);
  // Back out of Stripe restores this page from the bfcache with `opening` still
  // set, which leaves the pay button disabled for good. Clear it on the way in.
  useEffect(() => onReturnFromCheckout(() => setOpening(false)), []);
  const [payError, setPayError] = useState<string | null>(null);

  // Analysis / rendering
  const [look, setLook] = useState<FunnelLook>(() => RESUME?.look ?? (DEMO ? { ...defaultLook(true), tone: 7 } : defaultLook()));
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [shots, setShots] = useState<AvatarShots | null>(DEMO ? DEMO_SHOTS : null);
  /** Lead fires once per visit, on the first avatar. */
  const ledRef = useRef(false);
  // A resumed visit asks for its capture straight away: the look is back, the
  // avatar is not, and the paywall wants it.
  const [captureToken, setCaptureToken] = useState(RESUME ? 1 : 0);
  const [problem, setProblem] = useState<string | null>(null);

  const step = STEPS[cur];
  /**
   * How far this parent got, on the profile.
   *
   * Only ever moves forward: stepping back to change an answer must not make
   * the record say they got less far than they did. The screen NAME as well as
   * the number, because "step 7" means nothing in a dropdown six months from
   * now and "paywall" means exactly what it says.
   */
  const furthest = useRef(-1);
  useEffect(() => {
    analytics.step(step.type, step.key, cur);
    if (cur <= furthest.current) return;
    furthest.current = cur;
    analytics.person({ 'Furthest Screen': step.key ?? step.type, 'Screens Seen': cur + 1 });
    analytics.personUnion({ 'Screens': [step.key ?? step.type] });
    collect.session('onboarding', {
      furthest_screen: step.key ?? step.type,
      screens_seen: cur + 1,
      reached_paywall: step.type === 'pay',
    });
  }, [step.type, step.key, cur]);
  /**
   * The visit as material rather than as counters: what was answered, what the
   * model read out of the photo, which plan was in front of them. `collect`
   * coalesces these, so a tap per screen is not a request per tap.
   */
  useEffect(() => {
    if (DEMO) return;
    collect.session('onboarding', {
      distinct_id: analytics.distinctId(),
      child_name: name.trim() || undefined,
      answers,
      // Only once a photo has been read: a reload keeps the visit id, and the
      // fresh page's default look must not overwrite the one drawn from the photo.
      look: analysis ? look : undefined,
      analysis: analysis ?? undefined,
      plan,
      resumed: !!RESUME,
    });
  }, [answers, name, look, analysis, plan]);
  useEffect(() => {
    analytics.register({ funnel: 'onboarding' });
    if (RESUME) analytics.register({ resumed: true });
    if (DEMO) analytics.register({ demo: true });
    if (DEMO) return;
    // One row per parent even if they walk both funnels, and a first-seen that
    // never moves once it is set.
    analytics.personOnce({ 'First Seen': new Date().toISOString() });
    analytics.personUnion({ 'Funnels': ['onboarding'] });
  }, []);
  // Purchase: once per Checkout session, with the plan remembered before the
  // redirect. The success URL carries the session id, which keys the guard.
  useEffect(() => {
    if (step.type !== 'done') return;
    const sid = new URLSearchParams(location.search).get('session_id') || 'unknown';
    if (!firstSightOf(sid)) return;
    const plan = recallPlan();
    track('Purchase', { value: plan ? PLAN_AMOUNT[plan] : 0, currency: 'USD', content_name: plan ?? 'unknown' });
    analytics.event('Purchase Completed', { plan: plan ?? 'unknown', value: plan ? PLAN_AMOUNT[plan] : 0 });
    analytics.person({ 'Purchased': true, 'Plan': plan ?? 'unknown', 'Purchased At': new Date().toISOString() });
    collect.session('onboarding', { purchased: true, purchased_at: new Date().toISOString(), plan: plan ?? undefined });
  }, [step.type]);

  /**
   * Test mode: seven taps on the paywall's preview within ten seconds unlock
   * a $1 plan beneath the real ones, so a real card can be run end to end
   * without paying the real price. Nothing else changes; the plan is just one
   * more card.
   */
  const [testMode, setTestMode] = useState(RESUME?.testMode ?? false);
  const taps = useRef<number[]>([]);
  const testTap = useCallback(() => {
    const now = Date.now();
    taps.current = [...taps.current.filter((t) => now - t < 10000), now];
    if (taps.current.length >= 7) { setTestMode(true); taps.current = []; analytics.register({ testMode: true }); analytics.event('Test Mode Unlocked'); collect.session('onboarding', { test_mode: true }); }
  }, []);

  /** Move by `dir`, stepping over any step that does not apply. */
  const move = useCallback((from: number, dir: 1 | -1) => {
    let i = from + dir;
    while (i > 0 && i < STEPS.length - 1 && !visible(i)) i += dir;
    return Math.max(0, Math.min(STEPS.length - 1, i));
  }, [visible]);

  const go = useCallback((i: number) => setCur(Math.max(0, Math.min(STEPS.length - 1, i))), []);
  const next = useCallback(() => setCur((c) => move(c, 1)), [move]);
  const back = useCallback(() => setCur((c) => move(c, -1)), [move]);

  /**
   * Hand off to Stripe.
   *
   * `opening` stays true on the way out: this only ever resolves when the
   * redirect did NOT happen, so re-enabling the button on the happy path would
   * mean flashing it live again under a page that is already leaving.
   */
  const pay = useCallback(() => {
    if (opening) return;
    setOpening(true);
    setPayError(null);
    rememberPlan(plan);
    saveSnapshot({ answers, name, look, plan, testMode });
    track('InitiateCheckout', { value: PLAN_AMOUNT[plan], currency: 'USD', content_name: plan });
    analytics.event('Checkout Started', { plan, value: PLAN_AMOUNT[plan] });
    collect.session('onboarding', { checkout_started: true, plan });
    void startCheckout(plan).then((err) => {
      if (!err) return;
      setPayError(err);
      setOpening(false);
    });
  }, [opening, plan, answers, name, look, testMode]);

  const childWord = one(answers, 'pronouns') === 'she' ? 'your girl'
    : one(answers, 'pronouns') === 'he' ? 'your boy' : 'your child';
  const nm = name.trim() || childWord;

  // The narration spoken to this child takes a few seconds to make the first
  // time a name is seen, so it is asked for as soon as the name is typed and
  // the parent has moved on — by the catalogue it is usually ready.
  useEffect(() => {
    if (DEMO || !name.trim() || STEPS[cur]?.type === 'name' || cur <= STEPS.findIndex((s) => s.type === 'name')) return;
    void requestNarration(CARTOON_ID, name);
  }, [cur, name]);
  const pos = `${nm}’s`;

  const progress = useMemo(() => {
    if (['reveal', 'stream', 'pay', 'done'].includes(step.type)) return 100;
    // Counted against the steps that ACTUALLY apply, so skipping the faith
    // branch does not leave the bar short of 100.
    let done = 0;
    let total = 0;
    for (let k = 1; k < STEPS.length; k++) {
      const st = STEPS[k];
      if (!COUNTED.includes(st.type) || (st.when && !st.when(answers))) continue;
      total += 1;
      if (k <= cur) done += 1;
    }
    return total ? Math.round((done / total) * 100) : 0;
  }, [cur, step.type, answers]);

  // The paywall's preview flips through the whole catalogue, one title a
  // second, starting on the promised first cartoon. Only ticks on that step.
  const [previewIdx, setPreviewIdx] = useState(() => Math.max(0, POSTERS.findIndex((p) => p.id === PREVIEW_POSTER_ID)));
  useEffect(() => {
    if (step.type !== 'pay') return;
    const t = setInterval(() => setPreviewIdx((i) => (i + 1) % POSTERS.length), 1000);
    return () => clearInterval(t);
  }, [step.type]);
  const previewPoster = POSTERS[previewIdx];

  // ── The real analysis ──────────────────────────────────────────────────────
  /**
   * Runs once when the `gen` step mounts. The funnel must never dead-end on it:
   * a missing key, an offline box or a model that answers nonsense all fall back
   * to a look built from the funnel's own boy/girl answer, with the reason shown.
   */
  const analysing = useRef(false);
  useEffect(() => {
    if (step.type !== 'gen' || analysing.current) return;
    analysing.current = true;
    setProblem(null);

    const fallback = defaultLook(one(answers, 'pronouns') !== 'he');
    let cancelled = false;

    void (async () => {
      let nextLook = fallback;
      if (!photo) {
        setProblem('No photo was provided, so the character uses the default look.');
      } else {
        try {
          const preset = getPreset('character');
          const res = await analyzePhoto({
            provider: ANALYSIS_PROVIDER, model: ANALYSIS_MODEL, imageDataUrl: photo,
            systemPrompt: preset.system, userPrompt: preset.user, jsonMode: preset.jsonMode,
          });
          const parsed = tryParseResult(res.text);
          if (!parsed) {
            setProblem('The model did not return a usable result, so we used the default look.');
          } else {
            const warnings = validateResult(parsed);
            if (warnings.length) {
              setProblem(`Some values were outside the character vocabulary and were skipped: ${warnings.join('; ')}`);
            }
            if (!cancelled) setAnalysis(parsed);
            nextLook = lookFromAnalysis(parsed, fallback);
          }
        } catch (err) {
          setProblem(err instanceof Error ? err.message : 'The analysis request failed, so we used the default look.');
        }
      }
      if (cancelled) return;
      analytics.event('Avatar Created', { analysed: nextLook !== fallback, hadPhoto: !!photo });
      analytics.person({ 'Has Avatar': true, 'Avatar From Photo': !!photo });
      setLook(nextLook);
      // Painting is async inside the stage; ask for the capture and let it retry
      // until the composited hair has actually landed.
      setCaptureToken((t) => t + 1);
    })();

    return () => { cancelled = true; };
    // Deliberately keyed on the step only: re-running mid-analysis would race itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.type]);

  // Leave the gen step as soon as there is a character to show. A capture that
  // never succeeds still advances (after the stage gives up) — the reveal then
  // shows the photo-free fallback rather than hanging on a spinner.
  useEffect(() => {
    if (step.type === 'gen' && shots) {
      if (!ledRef.current) { ledRef.current = true; track('Lead'); collect.avatar('onboarding', shots.full); }
      const t = setTimeout(next, 900);
      return () => clearTimeout(t);
    }
  }, [step.type, shots, next]);

  const onCaptureFailed = useCallback((reason: string) => {
    setProblem((p) => p ?? reason);
    setShots(null);
    setCur((c) => (STEPS[c].type === 'gen' ? c + 1 : c));
  }, []);

  const restart = () => {
    setAnswers({}); setName(''); setPhoto(null); setPlan('year');
    setAnalysis(null); setShots(null); setProblem(null); setLook(defaultLook());
    setOpening(false); setPayError(null);
    analysing.current = false;
    clearSnapshot();
    // Drop `?checkout=…` as well, or the next reload would jump straight back
    // to the screen this button exists to leave.
    if (checkoutReturn()) history.replaceState(null, '', location.pathname);
    go(0);
  };

  // Stepping back into the questions arms the analysis again, so a changed photo
  // is re-read on the next pass through `gen`.
  useEffect(() => {
    if (['hero', 'choice', 'multi', 'name', 'fact', 'photo'].includes(step.type)) analysing.current = false;
  }, [step.type]);

  // The reveal steps back past `gen`, which would immediately re-run itself.
  const backTo = () => (step.type === 'reveal' ? go(move(cur, -1) - 1) : back());

  const wide = step.type === 'stream';

  return (
    <ToneContext.Provider value={look.tone}>
    <div className={`funnel${design === 'v1' ? '' : ` ${design}`}`}>
      <Bokeh />
      <AvatarStage look={look} captureToken={captureToken} onShots={setShots} onCaptureFailed={onCaptureFailed} />

      <div className={`stage${wide ? ' stage--wide' : ''}`}>
        <div className="top">
          <button className="back" onClick={backTo} aria-label="Back" hidden={cur === 0 || step.type === 'gen' || step.type === 'done'}>‹</button>
          <div className="bar"><span style={{ transform: `scaleX(${progress / 100})` }} /></div>
          <div className="wordmark">CartoonTime<b>+</b></div>
        </div>

        <div className="screen" key={cur}>
          {step.type === 'hero' && (
            <div className="hero">
              <div className="kicker">Made from one photo</div>
              <div className="herostage">
                <Clip src="/clips/hero-montage.mp4" className="heroclip" />
                <HeroPhoto />
              </div>
              <h1>Your child, starring in their own cartoon</h1>
              <p>Upload one photo of your child. We turn them into a cartoon character and make a new episode with them every month.</p>
              <button className="cta" onClick={() => {
                // Leaving the opening screen: the first drop-off worth watching,
                // and the moment the funnel proper begins.
                trackCustom('Second_screen_presented');
                next();
              }}>Turn your child into a cartoon ✨</button>
              <div className="ghost">Under 2 minutes · No card required</div>
            </div>
          )}

          {step.type === 'choice' && step.key && (
            <div>
              <div className="eyebrow">{step.eyebrow}</div>
              <h1 className="q">{fill(step.q, nm)}</h1>
              {step.sub && <p className="sub">{fill(step.sub, nm)}</p>}
              <div className={`opts${step.grid ? ' grid2' : ''}`}>
                {optionsFor(step, answers).map((o) => (
                  <button
                    key={o.v}
                    className={`opt${one(answers, step.key!) === o.v ? ' sel' : ''}`}
                    onClick={() => {
                      // Changing the age invalidates the topics chosen for the
                      // previous one, and changing the faith group invalidates
                      // its branch. Clearing beats showing a stale answer.
                      setAnswers((a) => {
                        const nextA: Answers = { ...a, [step.key!]: o.v };
                        if (step.key === 'age' && one(a, 'age') !== o.v) delete nextA.topics;
                        if (step.key === 'faithGroup' && one(a, 'faithGroup') !== o.v) delete nextA.faithBranch;
                        if (step.key === 'faith' && o.v !== 'yes') { delete nextA.faithGroup; delete nextA.faithBranch; }
                        return nextA;
                      });
                      analytics.answer(step.key!, o.v);
                      setTimeout(next, 240);
                    }}
                  >
                    {o.emo && <span className="emo">{o.emo}</span>}
                    <span className="optlabel">
                      {o.label}
                      {o.copy && <i>{o.copy}</i>}
                    </span>
                    {!step.grid && <span className="tick">{one(answers, step.key!) === o.v ? '✓' : ''}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step.type === 'multi' && step.key && (
            <MultiStep
              step={step}
              options={optionsFor(step, answers)}
              picked={many(answers, step.key)}
              onToggle={(v) => setAnswers((a) => {
                const cur2 = many(a, step.key!);
                const has = cur2.includes(v);
                return { ...a, [step.key!]: has ? cur2.filter((x) => x !== v) : [...cur2, v] };
              })}
              onContinue={() => { analytics.answer(step.key!, many(answers, step.key!)); next(); }}
              name={nm}
            />
          )}

          {step.type === 'fact' && step.fact && (
            <div className="factscreen">
              {/* The "children copy what they watch" fact plays the clips of the picked
                  topics that have one, in turn: the child doing those very things. */}
              <Clip srcs={(step.fact === FACTS[1] && topicClipsFor(many(answers, 'topics'), one(answers, 'age'))) || [step.fact.clip]} className={step.fact.portrait ? 'factclip portrait' : 'factclip'} />
              <h1>{fill(step.fact.title, nm)}</h1>
              <p>{fill(step.fact.body, nm)}</p>
              {step.fact.note && <p className="note">{step.fact.note}</p>}
              <button className="cta" onClick={next}>Got it</button>
            </div>
          )}

          {step.type === 'name' && (
            <div>
              <div className="eyebrow">Almost there</div>
              <h1 className="q">What is your child’s name?</h1>
              <p className="sub">We’ll weave it into the story and the hero says it out loud.</p>
              <input
                className="field" placeholder="e.g. Emma" autoComplete="off" autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) next(); }}
              />
              <button className="cta" disabled={!name.trim()} onClick={() => {
                const child = name.trim();
                analytics.event('Name Entered', { name: child });
                analytics.register({ childName: child });
                // The first moment there is a person to remember rather than a
                // page view, so this is where the profile is created.
                analytics.person({ $name: child, 'Child Name': child });
                next();
              }}>Continue</button>
            </div>
          )}

          {step.type === 'photo' && (
            <PhotoStep photo={photo} setPhoto={setPhoto} pos={pos} onContinue={() => { analytics.event('Photo Uploaded'); collect.photo('onboarding', photo); next(); }} />
          )}

          {step.type === 'gen' && (
            <div className="gen">
              <div className="scan">{photo && <img src={photo} alt="" />}</div>
              <h1 className="q" style={{ fontSize: 24 }}>Creating {pos} avatar…</h1>
              {problem && <div className="warn">{problem}</div>}
            </div>
          )}

          {step.type === 'reveal' && (
            <>
              <RevealStep name={nm} pos={pos} head={shots?.head ?? null} onPlay={next} />
              {!shots && problem && <div className="warn">{problem}</div>}
            </>
          )}

          {step.type === 'stream' && (
            <StreamStep name={nm} shots={shots} look={look} age={one(answers, 'age')} topics={many(answers, 'topics')} onPlay={next} />
          )}

          {step.type === 'pay' && (
            <div className="pay">
              <div style={{ position: 'relative' }} className="previewcycle" key={previewPoster.id} onPointerDown={testTap}>
                <PosterCard poster={previewPoster} name={nm} shots={shots} big />
                <span className="rpill"><i />PREVIEW</span>
                <span className="rcap">{previewPoster.title(nm)}</span>
              </div>
              <h1>{pos} first cartoon is ready 🎬</h1>
              <p className="lead">Unlock it and start {pos} adventure today.</p>
              {testMode && <div className="warn">Test mode: the $1 plan below charges a real card one dollar.</div>}
              <div className="plans">
                {[...PLANS, ...(testMode ? [TEST_PLAN] : [])].map((p) => (
                  <button
                    key={p.id}
                    className={`plan${plan === p.id ? ' sel' : ''}`}
                    aria-pressed={plan === p.id}
                    onClick={() => { setPlan(p.id); analytics.answer('plan', p.id); }}
                  >
                    {p.tag && <span className="tag">{p.tag}</span>}
                    <div className="row">
                      <span className="name">{p.name}</span>
                      <span className="price">{p.price}<small>{p.per}</small></span>
                    </div>
                    <div className="note">{p.note}</div>
                  </button>
                ))}
              </div>
              <ul className="feats">
                <li><span className="ck">✓</span><span>A new <b>personalized cartoon every week</b> + a special seasonal episode every month</span></li>
                <li><span className="ck">✓</span><span>Watch {nm} become the hero of their very own adventures</span></li>
                <li><span className="ck">✓</span><span>Unlimited replays</span></li>
                <li><span className="ck">✓</span><span>Ad-free</span></li>
                <li><span className="ck">✓</span><span>Safe for kids</span></li>
              </ul>
              {/* The button and the line under it stick together: naming the
                  wallets is only useful BEFORE the tap, and a button that
                  sticks on its own would cover that line until the page was
                  scrolled to its very end. */}
              <div className="paybar">
                <button className="cta" onClick={pay} disabled={opening}>
                  {opening ? 'Opening secure checkout…' : `Watch ${pos} First Cartoon.`}
                </button>
                {/* Named rather than drawn: the wallet a parent gets is
                    whichever one their device actually has, and it appears on
                    Stripe's page — promising a button with a logo we render
                    here would be promising something this screen does not
                    control. */}
                <div className="wallets">Pay with Apple&nbsp;Pay, Google&nbsp;Pay or card</div>
              </div>
              {payError && <div className="warn">{payError}</div>}
              <div className="trust">
                🔒 Cancel in one tap · We never store photos · GDPR compliant<br />
                Subscription renews automatically until you cancel. Prices exclude VAT where it applies.
              </div>
            </div>
          )}

          {step.type === 'done' && (
            /* Only reachable by coming back from Stripe with a completed
               payment. For now the message is that the service is not yet
               available in the parent's region and the purchase is refunded
               (Jernej, 2026-09-08); the Purchase event still fires above. */
            <div className="done">
              <div className="card">
                <img className="mark" src="/logo-mark.webp" alt="" draggable={false} />
                <div className="kicker">CartoonTime+</div>
                <h1>We are sorry, CartoonTime+ is not available in your region yet.</h1>
                <p className="lead">We will refund your purchase immediately.</p>
                <p className="help">If you have any questions, please email us at <a href="mailto:support@cartoontimeplus.com">support@cartoontimeplus.com</a></p>
                <button className="cta" onClick={restart}>OK</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Comparing two designs from two screenshots does not work — the
          difference is mostly motion, which a still cannot show. This flips
          them on the same screen, mid-flow, with state intact. */}
      {import.meta.env.DEV && (
        <div className="design-switch" role="group" aria-label="Design version">
          {DESIGNS.map((d) => (
            <button
              key={d}
              className={design === d ? 'on' : ''}
              aria-pressed={design === d}
              onClick={() => setDesign(d)}
            >
              {d}
            </button>
          ))}
        </div>
      )}
    </div>
    </ToneContext.Provider>
  );
}

// ── Steps that carry their own behaviour ──────────────────────────────────────

function PhotoStep({
  photo, setPhoto, pos, onContinue,
}: {
  photo: string | null; setPhoto: (v: string | null) => void;
  pos: string;
  onContinue: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);

  const pick = (f: File | undefined) => {
    if (!f) return;
    const r = new FileReader();
    r.onload = (ev) => setPhoto(String(ev.target?.result ?? ''));
    r.readAsDataURL(f);
  };

  return (
    <div>
      <div className="eyebrow">Last step</div>
      <h1 className="q">Upload a photo</h1>
      <p className="sub">One clear photo of their face. We turn it into {pos} avatar.</p>

      {photo
        ? <img className="preview" src={photo} alt="preview" />
        : (
          <label className="drop" htmlFor="funnel-file">
            <div className="ic">📸</div>
            <b>Add a photo</b>
            <small>JPG or PNG · well-lit face</small>
          </label>
        )}
      <input
        ref={fileRef} id="funnel-file" type="file" accept="image/*" style={{ display: 'none' }}
        onChange={(e) => pick(e.target.files?.[0])}
      />
      {photo && <button className="ghost" onClick={() => fileRef.current?.click()}>Change photo</button>}

      {/*
        The disclosure stayed when the consent checkbox went: it is what the
        paywall's "we never store photos" promise rests on, and a parent should
        read it before the upload, not after. It no longer GATES the step —
        if an explicit opt-in is ever required, this is where it goes back.
      */}
      <p className="disclosure">
        The photo is used once, to create the avatar, and is <b>not stored</b>. Upload it only if you are the
        parent or guardian.
      </p>

      <button className="cta" disabled={!photo} onClick={onContinue}>Create avatar ✨</button>
    </div>
  );
}

/**
 * The reveal, including the prototype's smoke-and-sparks canvas.
 *
 * Kept verbatim in behaviour — it is the moment the funnel is built around — but
 * the head in the frame is now the real captured character.
 */
function RevealStep({ name, pos, head, onPlay }: { name: string; pos: string; head: string | null; onPlay: () => void }) {
  const fxRef = useRef<HTMLCanvasElement>(null);
  const flashRef = useRef<HTMLDivElement>(null);
  // Five taps on the avatar open the A/B picker for the cartoon gate (QA).
  const { tap: secretTap, open: pickerOpen, close: closePicker } = useSecretTaps(5);

  useEffect(() => {
    const cv = fxRef.current;
    if (!cv) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;

    // Particle colours come from the stylesheet, so a design version owns its
    // own smoke and sparks instead of the purple being baked into the canvas.
    const css = getComputedStyle(cv);
    const smokeRGB = (css.getPropertyValue('--fx-smoke').trim() || '150,95,235');
    const goldRGB = (css.getPropertyValue('--fx-spark').trim() || '255,205,120');

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = cv.getBoundingClientRect();
    cv.width = Math.max(1, Math.round(rect.width * dpr));
    cv.height = Math.max(1, Math.round(rect.height * dpr));
    const cx = cv.width / 2;
    const cy = cv.height / 2;

    interface Puff { x: number; y: number; r: number; vx: number; vy: number; life: number; max: number; gold: boolean }
    interface Spark { x: number; y: number; vx: number; vy: number; r: number; life: number; max: number; tw: number }
    const smoke: Puff[] = [];
    const sparks: Spark[] = [];
    const t0 = performance.now();
    let bursted = false;
    let raf = 0;

    const addSmoke = (n: number) => {
      for (let i = 0; i < n; i++) {
        const a = Math.random() * 6.2832;
        const rr = (10 + Math.random() * 45) * dpr;
        smoke.push({
          x: cx + Math.cos(a) * rr, y: cy + Math.sin(a) * rr + 25 * dpr,
          r: (28 + Math.random() * 46) * dpr,
          vx: (Math.random() - .5) * .5 * dpr, vy: -(0.25 + Math.random() * 0.7) * dpr,
          life: 0, max: 850 + Math.random() * 650, gold: Math.random() < 0.42,
        });
      }
    };
    const addSparks = (n: number, spread: number) => {
      for (let i = 0; i < n; i++) {
        const a = Math.random() * 6.2832;
        const sp = (1.5 + Math.random() * spread) * dpr;
        sparks.push({ x: cx, y: cy, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, r: (1 + Math.random() * 2.4) * dpr, life: 0, max: 650 + Math.random() * 750, tw: Math.random() * 6.28 });
      }
    };

    const loop = (now: number) => {
      const t = now - t0;
      ctx.clearRect(0, 0, cv.width, cv.height);
      ctx.globalCompositeOperation = 'lighter';
      if (t < 1500) addSmoke(3); else if (t < 2500) addSmoke(1);
      if (!bursted && t > 1400) { bursted = true; addSparks(64, 9); flashRef.current?.classList.add('on'); }
      // No ambient trickle after the burst: a spark drifting across the face
      // for as long as the step is open looks like a blemish, not magic.

      for (let i = smoke.length - 1; i >= 0; i--) {
        const p = smoke[i];
        p.life += 16; p.x += p.vx; p.y += p.vy; p.vy *= 0.992; p.r += 0.35 * dpr;
        const lr = p.life / p.max;
        if (lr >= 1) { smoke.splice(i, 1); continue; }
        const al = Math.sin(Math.min(lr, 1) * Math.PI) * 0.42;
        const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r);
        const col = p.gold ? goldRGB : smokeRGB;
        g.addColorStop(0, `rgba(${col},${al})`);
        g.addColorStop(1, `rgba(${col},0)`);
        ctx.fillStyle = g;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, 6.2832); ctx.fill();
      }
      for (let j = sparks.length - 1; j >= 0; j--) {
        const s = sparks[j];
        s.life += 16; s.x += s.vx; s.y += s.vy; s.vx *= 0.955; s.vy = s.vy * 0.955 + 0.03 * dpr;
        const sr = s.life / s.max;
        if (sr >= 1) { sparks.splice(j, 1); continue; }
        const a2 = (1 - sr) * (0.6 + 0.4 * Math.sin(now / 70 + s.tw));
        ctx.fillStyle = `rgba(${goldRGB},${Math.max(0, a2)})`;
        ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, 6.2832); ctx.fill();
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div className="reveal">
      <div className="magic">
        <div className="halo" />
        <div className="frame revealing">
          <div className="av" onClick={secretTap}>{head && <img src={head} alt={name} />}</div>
        </div>
        <svg className="ring" viewBox="0 0 230 230"><circle cx="115" cy="115" r="110" /></svg>
        {pickerOpen && <GatePicker onClose={closePicker} />}
        <div className="flash" ref={flashRef} />
        <canvas className="fx" ref={fxRef} />
      </div>
      <h1>Meet <span>{name}</span>! 🎉</h1>
      <p>The avatar is ready. See it come to life in the very first personalized cartoon.</p>
      <button className="cta rvcta" onClick={onPlay}>Play {pos} cartoon ▶</button>
      <div className="ghost rvghost">First episode is ready</div>
    </div>
  );
}

/** True on a phone-width viewport — the billboard's one genuine layout switch. */
function useNarrow(): boolean {
  const [narrow, setNarrow] = useState(() => window.matchMedia('(max-width: 699px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 699px)');
    const on = () => setNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return narrow;
}

/**
 * The catalogue — the payoff screen. Every title on it is the child's, because
 * every poster composites the same captured character over its own scene.
 *
 * PLAY (billboard or any poster) is what opens the paywall.
 */
/**
 * The cartoon on the billboard: the first cartoon, already made, with the
 * child in it. It plays in the browser (see cartoon/CartoonPlayer.tsx). How
 * much of it plays before it asks to be unlocked — which opens the paywall,
 * the same conversion moment PLAY always was — is the `cartoon_gate`
 * experiment (see cartoon/gate.ts).
 */
function StreamStep({
  name, shots, look, age, topics, onPlay,
}: { name: string; shots: AvatarShots | null; look: FunnelLook; age: string; topics: string[]; onPlay: () => void }) {
  // Where the cartoon is not deployed (no manifest on this origin), the
  // billboard is the poster it always was.
  const [cartoon, setCartoon] = useState(true);
  const gate = useCartoonGate();
  const lockedRef = useRef(false);
  const onLocked = useCallback(() => {
    if (lockedRef.current) return;
    lockedRef.current = true;
    analytics.event('Cartoon Gate Reached');
  }, []);
  const onPhase = useCallback((p: string) => {
    if (p === 'playing') analytics.event('Cartoon Play');
    if (p === 'ended') analytics.event('Cartoon Ended');
  }, []);
  // The topics of the child's age band that have a poster, the picked ones
  // first: what this parent said they want help with, in their own shelf.
  const band = (TOPICS_BY_AGE[age] ?? []).map((o) => o.v).filter((v) => TOPIC_POSTERS[v]);
  const nextWins = [...topics.filter((t) => band.includes(t)), ...band.filter((t) => !topics.includes(t))].map((t) => TOPIC_POSTERS[t]);
  // The billboard's two layouts differ in WHERE the character stands, which is
  // data rather than styling — so it is chosen here and not in a media query.
  const narrow = useNarrow();
  const hero = { id: 'hero', title: () => name, scene: HERO_SCENE, avatar: HERO_AVATAR } as Poster;
  return (
    <div className="stream">
      <div className="brand">
        <span className="dot" />
        <span>CartoonTime<b>+</b></span>
        <span className="me">{name}’s profile</span>
      </div>

      {cartoon ? (
        <div className="billboard billboard--cartoon">
          <CartoonPlayer
            cartoon={CARTOON_ID}
            look={look}
            name={name}
            freeScenes={freeScenesFor(gate)}
            onLocked={onLocked}
            onUnlock={onPlay}
            onUnavailable={() => setCartoon(false)}
            onPhase={onPhase}
            onWatch={trackWatch}
            onScene={trackScene}
            lockTitle={`Unlock the rest of ${name}’s adventure`}
            lockButton="Watch the whole cartoon"
          />
          {/* Not an <h2>: the billboard's key-art lettering is styled on
              its h2, and a caption under a video is a caption, not a poster. */}
          <div className="cartoon-cap">
            <div className="cartoon-title">
              <strong>{name} at the Zoo</strong>
              <p>Episode 1 · {name}’s first cartoon, ready now</p>
            </div>
            <button className="info" onClick={onPlay}>Unlock all episodes</button>
          </div>
        </div>
      ) : (
        <div className="billboard">
          <span className="art">{hero.scene}</span>
          <Child shots={shots} at={narrow ? HERO_AVATAR_NARROW : HERO_AVATAR} costume={HERO_COSTUME} />
          <span className="scrim" />
          <div className="copy">
            <h2>Super {name}</h2>
            <p>{HERO_TAGLINE}</p>
            <div className="row">
              <button className="play" onClick={onPlay}>▶ PLAY</button>
              <button className="info" onClick={onPlay}>ⓘ INFO</button>
            </div>
          </div>
        </div>
      )}

      {nextWins.length > 0 && (
        <div className="shelf">
          <h3>What {name} should master next</h3>
          <div className="rail">
            {nextWins.map((p) => (
              <div className="card" key={p.id}>
                <PosterCard poster={p} name={name} shots={shots} onClick={onPlay} />
                <PosterLabel text={p.title(name)} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="shelf">
        <h3>My list</h3>
        <div className="rail">
          {POSTERS.map((p) => (
            <div className="card" key={p.id}>
              <PosterCard poster={p} name={name} shots={shots} onClick={onPlay} />
              <PosterLabel text={p.title(name)} />
            </div>
          ))}
        </div>
      </div>

      <div className="shelf">
        <h3>Because {name} liked adventure</h3>
        <div className="rail">
          {[...POSTERS].reverse().map((p) => (
            <div className="card" key={`r-${p.id}`}>
              <PosterCard poster={p} name={name} shots={shots} onClick={onPlay} />
              <PosterLabel text={p.title(name)} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
